import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CalendarClock, Sparkles, PartyPopper, Mail } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/iariya-ai";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isCalendlyEvent = (e) =>
  e?.data?.event && typeof e.data.event === "string" && e.data.event.indexOf("calendly.") === 0;

const trackEvent = async (eventType, payload) => {
  // 1) Backend persistence
  try {
    await axios.post(`${API}/booking-event`, {
      event_type: eventType,
      payload: payload || {},
      referrer: document.referrer || null,
      user_agent: navigator.userAgent || null,
    });
  } catch (err) {
    // non-fatal
    console.warn("booking-event persist failed", err);
  }

  // 2) PostHog (preview env has it preloaded)
  try {
    if (window.posthog && typeof window.posthog.capture === "function") {
      window.posthog.capture(eventType, payload || {});
    }
  } catch {
    /* noop */
  }

  // 3) Generic dataLayer (GTM / GA4 ready)
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventType,
      ...(payload || {}),
    });
  } catch {
    /* noop */
  }
};

export const BookCalendar = () => {
  const containerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [scheduled, setScheduled] = useState(false);

  // Load Calendly widget + init
  useEffect(() => {
    const SCRIPT_ID = "calendly-widget-script";
    const STYLE_ID = "calendly-widget-style";

    if (!document.getElementById(STYLE_ID)) {
      const link = document.createElement("link");
      link.id = STYLE_ID;
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    }

    const initWidget = () => {
      if (window.Calendly && containerRef.current) {
        containerRef.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url: `${CALENDLY_URL}?hide_event_type_details=0&hide_gdpr_banner=1&primary_color=d6469b&text_color=1c1917&background_color=ffffff`,
          parentElement: containerRef.current,
        });
        setLoaded(true);
      }
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      initWidget();
    } else {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      script.onload = initWidget;
      document.body.appendChild(script);
    }
  }, []);

  // Listen to Calendly postMessage events
  useEffect(() => {
    const handler = (e) => {
      if (!isCalendlyEvent(e)) return;
      const eventName = e.data.event; // e.g. calendly.event_scheduled
      const payload = e.data.payload || {};

      if (eventName === "calendly.event_type_viewed") {
        trackEvent("booking_calendar_viewed", { source: "calendly" });
      }
      if (eventName === "calendly.date_and_time_selected") {
        trackEvent("booking_time_selected", { source: "calendly" });
      }
      if (eventName === "calendly.event_scheduled") {
        const inviteeUri = payload?.invitee?.uri || null;
        const eventUri = payload?.event?.uri || null;
        trackEvent("booking_confirmed", {
          source: "calendly",
          invitee_uri: inviteeUri,
          event_uri: eventUri,
        });
        setScheduled(true);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Allow demo/manual reveal of the thank-you for QA via ?booked=1
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("booked") === "1") setScheduled(true);
    } catch {
      /* noop */
    }
  }, []);

  return (
    <section
      id="book"
      data-testid="book-section"
      className="border-b-2 border-ink py-24 lg:py-32 bg-[var(--cream)] relative overflow-hidden"
    >
      <div className="absolute inset-0 confetti-bg opacity-[0.14] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        {!scheduled ? (
          <>
            <div className="text-center max-w-3xl mx-auto">
              <p
                className="uppercase text-sm font-bold tracking-[0.25em] text-[var(--pink)] inline-flex items-center gap-2"
                data-testid="book-eyebrow"
              >
                <Sparkles size={14} /> Book your slot
              </p>
              <h2
                className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight"
                data-testid="book-heading"
              >
                Pick a time that{" "}
                <span className="text-[var(--blue)]">suits you.</span>
              </h2>
              <p className="mt-5 text-lg text-stone-700">
                30 minutes. No prep needed. Bring your biggest time-suck and
                we'll sketch a fix on the call. If we're not a fit, we'll point
                you to someone who is.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-ink rounded-full font-bold shadow-brutal text-sm">
                <CalendarClock size={16} className="text-[var(--pink)]" />{" "}
                30-min video call
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--yellow)] border-2 border-ink rounded-full font-bold shadow-brutal text-sm">
                Free, no obligation
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--blue)] text-white border-2 border-ink rounded-full font-bold shadow-brutal text-sm">
                Get a tailored plan
              </span>
            </div>

            <div className="mt-12 mx-auto max-w-4xl">
              <div className="bg-white border-2 border-ink rounded-3xl shadow-brutal-lg overflow-hidden">
                <div
                  ref={containerRef}
                  data-testid="calendly-embed"
                  className="w-full"
                  style={{ minHeight: "720px" }}
                />
                {!loaded && (
                  <div className="p-10 text-center text-stone-600 font-semibold">
                    Loading the calendar…
                  </div>
                )}
              </div>
              <p className="text-center mt-6 text-stone-700">
                Prefer email?{" "}
                <a
                  href="mailto:eventinfo@iariya.com"
                  className="font-bold text-[var(--pink)] underline underline-offset-4"
                  data-testid="book-fallback-email"
                >
                  eventinfo@iariya.com
                </a>
              </p>
            </div>
          </>
        ) : (
          <ThankYou onResetTest={() => setScheduled(false)} />
        )}
      </div>
    </section>
  );
};

const ThankYou = ({ onResetTest }) => {
  return (
    <div
      data-testid="book-thankyou"
      className="max-w-3xl mx-auto bg-white border-2 border-ink rounded-3xl shadow-brutal-lg p-10 sm:p-14 text-center relative pop-in"
    >
      <div className="mx-auto h-20 w-20 rounded-full bg-[var(--yellow)] border-2 border-ink flex items-center justify-center shadow-brutal">
        <PartyPopper size={36} className="text-ink" />
      </div>

      <p className="uppercase text-sm font-bold tracking-[0.25em] text-[var(--pink)] mt-6">
        You're booked in!
      </p>
      <h2
        className="font-display mt-3 text-4xl sm:text-5xl font-black tracking-tight"
        data-testid="book-thankyou-heading"
      >
        Brilliant — see you <span className="text-[var(--blue)]">soon.</span>
      </h2>
      <p className="mt-5 text-lg text-stone-700">
        Check your inbox for the confirmation and calendar invite. We'll come
        prepared with a few quick wins for your business.
      </p>

      <div className="mt-8 grid sm:grid-cols-3 gap-3 text-left">
        <div className="border-2 border-ink rounded-2xl p-4 bg-[var(--cream)]">
          <p className="text-xs font-black uppercase tracking-widest text-stone-600">
            Step 1
          </p>
          <p className="font-display text-lg font-extrabold mt-1">
            Confirm in your inbox
          </p>
          <p className="text-sm text-stone-700 mt-1">
            Tap the calendar invite so it lands in your diary.
          </p>
        </div>
        <div className="border-2 border-ink rounded-2xl p-4 bg-[var(--cream)]">
          <p className="text-xs font-black uppercase tracking-widest text-stone-600">
            Step 2
          </p>
          <p className="font-display text-lg font-extrabold mt-1">
            Jot down your pain points
          </p>
          <p className="text-sm text-stone-700 mt-1">
            Two or three jobs that eat your time each week.
          </p>
        </div>
        <div className="border-2 border-ink rounded-2xl p-4 bg-[var(--cream)]">
          <p className="text-xs font-black uppercase tracking-widest text-stone-600">
            Step 3
          </p>
          <p className="font-display text-lg font-extrabold mt-1">
            Show up and chat
          </p>
          <p className="text-sm text-stone-700 mt-1">
            We'll do the rest. Promise.
          </p>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
        <a
          href="mailto:eventinfo@iariya.com"
          data-testid="book-thankyou-email"
          className="btn-ink inline-flex items-center gap-2 bg-[var(--pink)] text-white px-5 py-3"
        >
          <Mail size={18} /> Reach the team
        </a>
        <button
          onClick={onResetTest}
          data-testid="book-thankyou-book-another"
          className="btn-ink inline-flex items-center gap-2 bg-white text-ink px-5 py-3"
        >
          Book another slot
        </button>
      </div>

      <p className="mt-6 text-xs text-stone-500">
        Conversion tracked anonymously for analytics. No personal info shared.
      </p>
    </div>
  );
};
